// ── geoInfo: formato ÚNICO de la ubicación (y metadatos) de un fichaje ──────
// ARCHIVO COMPARTIDO: debe ser idéntico byte a byte en TemControlApp y
// TemControlFront (scripts/check-shared-libs.js lo vigila).
//
// El backend guarda `geoInfo` como texto libre y NO lo interpreta (salvo un
// `split(',')` en su validación de distancia). Por eso el front lo usa también
// para metadatos que no tienen columna propia. Formato:
//
//   "<cabeza>;clave=valor;clave=valor…"
//
//   cabeza  "lat,lng" o vacía. Las coordenadas van SIEMPRE PRIMERO: todos los
//           lectores antiguos toman lo que hay antes del primer ';'.
//   tokens  acc=<metros>          precisión declarada por el GPS/navegador
//           origen=<origen>       desde dónde se REGISTRÓ (ver GEO_ORIGIN)
//           apprby=<quién>:<ISO>  aprobador (URL-encoded)
//           modby=<quién>:<ISO>   último editor (URL-encoded)
//           reqdelete=1|0         solicitud de eliminación de un periodo
//           motivo=<texto>        nota del trabajador (URL-encoded)
//           recoverable=1|0, extra=1|0 …
//
// Ejemplos reales:
//   "37.41234,-5.95678;acc=16"                        fichaje en vivo (app ≤ 2.0.8)
//   "37.41234,-5.95678;acc=16;origen=live"            fichaje en vivo (app)
//   "37.41234,-5.95678;acc=40;origen=manual;motivo=Reuni%C3%B3n"
//   ";origen=manual"                                  manual sin ubicación
//   ";reqdelete=1;apprby=Admin:2026-07-08T13:00:24.300Z;recoverable=1"
//   " "                                               "vaciado" (el backend no admite null)
//
// Los valores de los tokens NO pueden contener ';' (quien escribe texto libre
// lo URL-encodea: motivo, apprby, modby).

export const GEO_ORIGIN = Object.freeze({
  LIVE: 'live',            // botón de fichar de la app
  WEB: 'web',              // botón de fichar de la web
  MANUAL: 'manual',        // fichaje manual / diferido del trabajador
  CORRECTION: 'correccion', // solicitud de corrección (y fichajes que faltaban)
});

// Orígenes en los que la ubicación es la del momento de REGISTRARLO, no la
// del instante del fichaje: se muestran como "Registrado desde".
const MANUAL_ORIGINS = new Set([GEO_ORIGIN.MANUAL, GEO_ORIGIN.CORRECTION]);

export const isManualOrigin = (origin) => MANUAL_ORIGINS.has(String(origin || '').toLowerCase());

// Decimales de las coordenadas guardadas: 6 ≈ 0,1 m. El GPS devuelve 14-15
// y la columna es un VARCHAR que comparte espacio con el resto de tokens.
const COORD_DECIMALS = 6;

const asText = (v) => (v == null ? '' : String(v));

const toNumber = (v) => {
  if (v == null) return null;
  const t = String(v).trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

const validLatLng = (lat, lng) => {
  const a = toNumber(lat);
  const b = toNumber(lng);
  if (a == null || b == null) return null;
  if (a < -90 || a > 90 || b < -180 || b > 180) return null;
  return { lat: a, lng: b };
};

const roundCoord = (n) => String(Number(n.toFixed(COORD_DECIMALS)));

// "lat,lng" (o el JSON {"lat":…,"lng":…} de la documentación antigua del
// backend) → { lat, lng } | null.
export const parseCoords = (head) => {
  const h = asText(head).trim();
  if (!h) return null;
  if (h[0] === '{') {
    try {
      const o = JSON.parse(h);
      return validLatLng(o?.lat ?? o?.latitude, o?.lng ?? o?.lon ?? o?.longitude);
    } catch {
      return null;
    }
  }
  const parts = h.split(',');
  if (parts.length !== 2) return null;
  return validLatLng(parts[0], parts[1]);
};

// Segmento "clave=valor" → { key, value } | null (la cabeza con coordenadas
// o texto libre no es un token).
const parseSegment = (seg) => {
  const s = asText(seg);
  const i = s.indexOf('=');
  if (i <= 0) return null;
  const key = s.slice(0, i).trim();
  if (!key || /[\s,{]/.test(key)) return null;
  return { key, value: s.slice(i + 1) };
};

const segmentsOf = (geoInfo) => asText(geoInfo).split(';');

// ── Tokens ──────────────────────────────────────────────────────────────────

// Valor del PRIMER token `key`, o null.
export const getToken = (geoInfo, key) => {
  for (const seg of segmentsOf(geoInfo)) {
    const t = parseSegment(seg);
    if (t && t.key === key) return t.value;
  }
  return null;
};

// ¿Hay algún token `key` (con valor `value`, si se indica)?
export const hasToken = (geoInfo, key, value) => segmentsOf(geoInfo).some((seg) => {
  const t = parseSegment(seg);
  return !!t && t.key === key && (value === undefined || t.value === String(value));
});

// Quita TODOS los tokens `key`. Conserva la cabeza y el resto de tokens tal
// cual. null/undefined → ''.
export const removeToken = (geoInfo, key) => {
  const segs = segmentsOf(geoInfo);
  const out = [];
  segs.forEach((seg, i) => {
    const t = parseSegment(seg);
    if (t && t.key === key) {
      if (i === 0) out.push(''); // la cabeza era el token: queda vacía
      return;
    }
    out.push(seg);
  });
  return out.join(';');
};

// Pone `key=value` al final, sustituyendo cualquier `key` previo. Con value
// null/undefined solo lo quita. Un ';' dentro del valor rompería el formato:
// se cambia por ','.
export const appendToken = (geoInfo, key, value) => {
  const base = removeToken(geoInfo, key);
  if (value == null) return base;
  return `${base};${key}=${String(value).replace(/;/g, ',')}`;
};

// ── Lectura ─────────────────────────────────────────────────────────────────

const decode = (v) => {
  if (v == null) return null;
  try { return decodeURIComponent(v); } catch { return v; }
};

// "Nombre:2026-06-08T11:30:00Z" → { who, at } (at = texto ISO o null).
const parseWhoAt = (v) => {
  if (v == null) return null;
  const s = String(v);
  const i = s.indexOf(':');
  const who = decode(i >= 0 ? s.slice(0, i) : s);
  if (!who) return null;
  const at = i >= 0 ? s.slice(i + 1) || null : null;
  return { who, at };
};

const LOCATION_KEYS = new Set(['acc', 'origen']);

// Lee cualquier geoInfo (actual o antiguo) sin lanzar nunca.
//   { lat, lng, hasCoords, accuracy, origin, isManual, approver, modifier,
//     reqdelete, motivo, tokens, rest }
// `rest`: los tokens que NO son de ubicación (apprby, reqdelete, motivo…)
// tal cual, con su ';' delante — lo que hay que conservar al re-ubicar.
export const parseGeoInfo = (geoInfo) => {
  const segs = segmentsOf(geoInfo);
  const headToken = parseSegment(segs[0]);
  const coords = headToken ? null : parseCoords(segs[0]);
  const tokens = {};
  const restParts = [];
  segs.forEach((seg, i) => {
    const t = parseSegment(seg);
    if (!t) {
      // Texto libre fuera de la cabeza (notas antiguas): se conserva.
      if (i > 0 && seg.trim()) restParts.push(seg);
      return;
    }
    if (!(t.key in tokens)) tokens[t.key] = t.value;
    if (!LOCATION_KEYS.has(t.key)) restParts.push(seg);
  });
  const origin = tokens.origen ? String(tokens.origen).trim().toLowerCase() || null : null;
  const accuracy = toNumber(tokens.acc);
  const reqdelete = hasToken(geoInfo, 'reqdelete', '1') ? true
    : (tokens.reqdelete != null ? false : null);
  return {
    lat: coords ? coords.lat : null,
    lng: coords ? coords.lng : null,
    hasCoords: !!coords,
    accuracy: coords && accuracy != null && accuracy >= 0 ? accuracy : null,
    origin,
    isManual: isManualOrigin(origin),
    approver: parseWhoAt(tokens.apprby),
    modifier: parseWhoAt(tokens.modby),
    reqdelete,
    motivo: tokens.motivo != null ? decode(tokens.motivo) : null,
    tokens,
    rest: restParts.map((p) => `;${p}`).join(''),
  };
};

// ── Escritura ───────────────────────────────────────────────────────────────

// Sustituye la UBICACIÓN de un geoInfo (cabeza + acc + origen) conservando
// intactos el resto de tokens (apprby, reqdelete, motivo…). Sin coordenadas
// válidas la cabeza queda vacía; sin `origin` se conserva el que hubiera.
// Una cabeza de texto libre antiguo (no coordenadas) se guarda como `nota=`
// para no perderla. Devuelve null si no queda nada.
export const withLocation = (geoInfo, { lat, lng, accuracy, origin } = {}) => {
  const segs = segmentsOf(geoInfo);
  const oldHead = parseSegment(segs[0]) ? '' : segs[0];
  const parsed = parseGeoInfo(geoInfo);
  const c = validLatLng(lat, lng);
  let out = c ? `${roundCoord(c.lat)},${roundCoord(c.lng)}` : '';
  if (oldHead.trim() && !parseCoords(oldHead)) out = appendToken(out, 'nota', encodeURIComponent(oldHead.trim()));
  const acc = toNumber(accuracy);
  if (c && acc != null && acc >= 0) out = appendToken(out, 'acc', Math.round(acc));
  const nextOrigin = origin || parsed.origin;
  if (nextOrigin) out = appendToken(out, 'origen', nextOrigin);
  out += parsed.rest;
  return out || null;
};

// Construye el geoInfo de un fichaje NUEVO.
//   buildGeoInfo({ lat: 37.412345678, lng: -5.95678, accuracy: 16.4, origin: 'manual' })
//     → "37.412346,-5.95678;acc=16;origen=manual"
// `extra`: tokens adicionales, como texto (";motivo=…") o como objeto
// ({ motivo: '…' }, valores ya codificados). Devuelve null si queda vacío.
export const buildGeoInfo = ({ lat, lng, accuracy, origin, extra } = {}) => {
  let tail = '';
  if (typeof extra === 'string') tail = extra;
  else if (extra && typeof extra === 'object') {
    Object.keys(extra).forEach((k) => { tail = appendToken(tail, k, extra[k]); });
  }
  // Solo los tokens de `extra`: su cabeza (si trae coordenadas) se ignora.
  const tokensOnly = parseGeoInfo(tail).rest;
  return withLocation(tokensOnly, { lat, lng, accuracy, origin });
};

// ── Cuándo pedir la ubicación ───────────────────────────────────────────────
// 'required': la empresa activó la geolocalización del trabajador
//             (geoTracking): sin ubicación NO se registra el fichaje, sea en
//             vivo, manual o una solicitud de corrección.
// 'off':      no se pide la ubicación (no se molesta con el permiso a quien
//             la empresa no geolocaliza). Los manuales se marcan igualmente
//             con su origen.
// ('optional' — pedirla sin bloquear — lo soporta captureGeoInfo por si se
// decide usarlo, pero hoy ninguna política lo devuelve.)
export const geoCaptureMode = (user) => (user && user.geoTracking ? 'required' : 'off');

export const GEO_REQUIRED_TITLE = 'Es necesario activar la ubicación';

// Texto del bloqueo por falta de ubicación. `detail`: el motivo concreto
// (permiso denegado, sin señal…). `manual`: fichaje manual o corrección.
export const geoRequiredMessage = (detail, { manual = false } = {}) => {
  const why = asText(detail).trim() || 'No se ha podido obtener tu ubicación.';
  const rule = manual
    ? 'Tu empresa exige registrar la ubicación en todos los fichajes, también en los fichajes manuales y en las solicitudes de corrección: se guarda desde dónde se registran.'
    : 'Tu empresa exige registrar la ubicación en todos los fichajes.';
  return `${why}\n\n${rule}`;
};

// Obtiene la posición con `getPosition()` (→ { lat, lng, accuracy }) según
// `mode` y devuelve el geoInfo a guardar:
//   { geoInfo, position, error, blocked }
// blocked=true solo en 'required' sin posición: quien llama NO debe registrar
// nada y tiene que avisar (geoRequiredMessage). En 'off' no se llama a
// getPosition. En 'optional' un fallo no bloquea (se guarda sin coordenadas).
export const captureGeoInfo = async ({ mode, origin, getPosition, extra = null } = {}) => {
  if (mode !== 'required' && mode !== 'optional') {
    return { geoInfo: buildGeoInfo({ origin, extra }), position: null, error: null, blocked: false };
  }
  try {
    if (typeof getPosition !== 'function') throw new Error('No se puede obtener la ubicación en este dispositivo.');
    const pos = await getPosition();
    const c = validLatLng(pos?.lat, pos?.lng);
    if (!c) throw new Error('No se ha podido determinar tu ubicación.');
    const position = { lat: c.lat, lng: c.lng, accuracy: toNumber(pos?.accuracy) };
    return {
      geoInfo: buildGeoInfo({ ...position, origin, extra }),
      position,
      error: null,
      blocked: false,
    };
  } catch (error) {
    if (mode === 'required') return { geoInfo: null, position: null, error, blocked: true };
    return { geoInfo: buildGeoInfo({ origin, extra }), position: null, error, blocked: false };
  }
};

// ── Presentación ────────────────────────────────────────────────────────────

// Lo que la UI necesita para pintar la ubicación de un fichaje.
//   missingLabel  texto si no hay coordenadas ("Sin ubicación" /
//                 "Manual · sin ubicación")
//   prefix        "Registrado desde" en manuales/correcciones (la posición es
//                 la del momento de registrarlo, no la del fichaje)
//   tag           etiqueta corta del origen manual ("Manual" / "Corrección")
export const describeGeoInfo = (geoInfo) => {
  const p = parseGeoInfo(geoInfo);
  const tag = p.origin === GEO_ORIGIN.CORRECTION ? 'Corrección'
    : (p.origin === GEO_ORIGIN.MANUAL ? 'Manual' : null);
  return {
    ...p,
    tag,
    prefix: p.isManual ? 'Registrado desde' : null,
    missingLabel: p.isManual ? `${tag} · sin ubicación` : 'Sin ubicación',
  };
};

// ── Ubicación en el motivo de una corrección ────────────────────────────────
// POST /api/correction no tiene campo geoInfo (solo `reason`, texto libre).
// La ubicación desde la que se pidió la corrección viaja al final del motivo:
//   "Olvidé fichar [geo:37.41234,-5.95678;acc=16;origen=correccion]"
// splitReasonGeo la separa para pintarla aparte. Si no cabe en `maxLength`
// (VARCHAR del backend) se prioriza el texto del trabajador.
const REASON_GEO_RE = /\s*\[geo:([^\]]*)\]\s*$/;

export const appendGeoToReason = (reason, geoInfo, maxLength = 255) => {
  const text = asText(reason).replace(REASON_GEO_RE, '');
  if (!parseGeoInfo(geoInfo).hasCoords) return text;
  const tag = ` [geo:${asText(geoInfo).replace(/[\]\r\n]/g, '')}]`;
  if (maxLength && text.length + tag.length > maxLength) return text;
  return `${text}${tag}`;
};

export const splitReasonGeo = (reason) => {
  const s = asText(reason);
  const m = REASON_GEO_RE.exec(s);
  if (!m) return { text: s, geoInfo: null };
  return { text: s.slice(0, m.index), geoInfo: m[1] || null };
};
